import { MPSProduct, MPSService, StatementResponse, StatementStatus } from "../../../types";
import centsToRand, {
  formatDatev2,
  getDurationText,
  getTotalAmountForMPSLineItem,
  getTotalAmountForMPSLineItemArray,
  getTotalVatAmountForMPSLineItemArray,
  getVatAmountForMPSLineItem,
  numberWithSpaces,
} from "../../../utils/utils";
import ikhokhaLogo from "../../../assets/logo.svg";
import styles from "./index.module.css";
import { Button } from "../../../components/Button";
import checkmarkIcon from "../../../assets/checkmark.svg";

interface StatementDesktopProps {
  statement: StatementResponse;
  products: MPSProduct[];
  services: MPSService[];
  onPayInvoice: Function;
  missingProductIds: string[];
  hasBlockingMissingItems: boolean;
  isStockAvailabilityLoading: boolean;
}

const StatementDesktop = ({
  statement,
  onPayInvoice,
  products,
  services,
  missingProductIds,
  hasBlockingMissingItems,
  isStockAvailabilityLoading,
}: StatementDesktopProps) => {
  const missingProductIdSet = new Set(missingProductIds);
  const isMissingProduct = (product: MPSProduct) => {
    const ids = [product.catalogueItemId];
    if (product.variant?.variantID) {
      ids.push(product.variant.variantID);
    }
    if (product.variant?.catalogueItemID) {
      ids.push(product.variant.catalogueItemID);
    }
    return ids.some(id => missingProductIdSet.has(id));
  };

  const invoiceReference = statement.statement.invoiceReference;
  const customerName = statement.statement.customer.customerName;
  const { streetAddress, city, postalCode } = statement.statement.customer.billingAddress;
  const compiledAddress = `${streetAddress}, ${city}, ${postalCode}`;
  const contactNumber = statement.statement.customer.contactNumber;
  const emailAddress = statement.statement.customer.emailAddress;

  const registeredName = statement.owner.companyName;
  const compiledMerchantAddress = `${statement.owner.address.street1}, ${statement.owner.address.city}, ${statement.owner.address.code}`;
  const vatNumber = statement.owner.vatNumber;
  const companyRegistrationNumber = statement.owner.companyRegistrationNumber;
  const dueDate = statement.statement.dueDate;
  const additionalNotes = statement.statement.additionalNotes;

  const shouldShowServicesTotal = products.length !== 0;
  const shouldShowProductsTotal = services.length !== 0;

  return (
    <>
      <div className={styles.header}>
        <img className={styles["header-img"]} src={ikhokhaLogo} alt="" />
      </div>

      {/* Paid Banner */}
      {statement.statement.status === StatementStatus.PAID && (
        <div className={styles["paid-banner-container"]}>
          <div className={styles["paid-banner"]}>
            <img className={styles["tick-icon"]} src={checkmarkIcon} />
            This invoice is already paid
          </div>
        </div>
      )}

      {hasBlockingMissingItems && (
        <div className={styles["paid-banner-container"]}>
          <div className={styles["unavailable-banner"]}>
            Some line items are no longer available. Please contact merchant.
          </div>
        </div>
      )}

      <div className={styles.container}>
        <div className={styles.card}>
          <div className={`${styles.flex} ${styles["space-between"]}`}>
            {/* Customer Details */}
            <div>
              <div className={styles["invoice-heading"]}>Merchant Invoice</div>
              <div className={`${styles["mb-20"]} ${styles["text-small"]}`}>{invoiceReference}</div>

              {/* Customer Details */}
              <div className={styles["customer-details-text"]}>
                <p className={styles["font-roboto-medium"]}>Bill to:</p>
                <p>{customerName}</p>
                {streetAddress && <p>{compiledAddress}</p>}
                {emailAddress && <p>Email: {emailAddress}</p>}
                {contactNumber && <p>Customer number: {contactNumber}</p>}
              </div>
            </div>

            {/* Merchant Details */}
            <div className={styles["text-right"]}>
              {statement.statement.businessLogo?.cropped ? (
                <img
                  style={{ height: "48px", objectFit: "contain" }}
                  src={`${process.env.REACT_APP_BUSINESS_LOGO_IMAGES_CLOUDFRONT}/${statement.statement.businessLogo.cropped}`}
                />
              ) : (
                <img className={styles["header-img"]} src={ikhokhaLogo} alt="" />
              )}
              <div
                className={`${styles["merchant-details-text"]} ${
                  statement.statement.businessLogo?.cropped ? `${styles["mt-8"]}` : `${styles["mt-26"]}`
                }`}
              >
                <div>{registeredName}</div>
                <div>
                  {compiledMerchantAddress} <br />
                </div>
                {vatNumber && <div>VAT no: {vatNumber}</div>}
                {companyRegistrationNumber && <div>Reg no: {companyRegistrationNumber}</div>}
              </div>
            </div>
          </div>

          {/* Due Date */}
          <div className={`${styles["due-date"]} ${styles["font-bold"]} ${styles["text-small"]} ${styles["mt-40"]}`}>
            Due: <span>{formatDatev2(dueDate)}</span>
          </div>

          <div className={`${styles["mt-40"]}`}>
            <div className={styles["line-items-heading"]}>Line Items</div>

            {/* Products Table */}
            {products.length > 0 && (
              <table className={`${styles["w-full"]} ${styles.table}`}>
                <thead className={`${styles["table-header"]} ${styles["text-small"]}`}>
                  <tr>
                    <th className={`${styles["top-bottom-left-rounded"]} ${styles["pl-8"]} ${styles["w-130"]}`}>Product(s)</th>
                    <th className={styles["w-100"]}>Variant(s)</th>
                    <th className={`${styles["text-center"]} ${styles["w-80"]}`}>Qty</th>
                    <th className={`${styles["text-right"]} ${styles["w-80"]}`}>VAT (15%)</th>
                    <th
                      className={`${styles["top-bottom-right-rounded"]} ${styles["text-right"]} ${styles["pr-8"]} ${styles["w-100"]}`}
                    >
                      Price (incl. VAT)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => {
                    const variantName = product.variant ? Object.values(product.variant.variantValues).join("/") : "-";
                    const productUnavailable = isMissingProduct(product);
                    return (
                      <tr
                        key={product.variant ? product.variant.variantID : product.catalogueItemId}
                        className={`${styles["table-row"]} ${styles["text-small"]} ${
                          productUnavailable ? styles["unavailable-row"] : ""
                        }`}
                      >
                        <td className={styles["item-name"]}>{product.name}</td>
                        <td> {variantName} </td>
                        <td className={styles["text-center"]}>{product.quantity}</td>
                        <td className={styles["text-right"]}>
                          {product.vat ? `R ${numberWithSpaces(centsToRand(getVatAmountForMPSLineItem(product)))}` : "-"}
                        </td>
                        <td className={styles["text-right"]}>
                          R{numberWithSpaces(centsToRand(getTotalAmountForMPSLineItem(product)))}
                        </td>
                      </tr>
                    );
                  })}
                  {shouldShowProductsTotal && (
                    <tr className={styles["table-total-row"]}>
                      <td>Subtotal</td>
                      <td></td>
                      <td></td>
                      <td className={styles["text-right"]}>
                        R {numberWithSpaces(centsToRand(getTotalVatAmountForMPSLineItemArray(products)))}
                      </td>
                      <td className={styles["text-right"]}>
                        R {numberWithSpaces(centsToRand(getTotalAmountForMPSLineItemArray(products)))}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Services Table */}
            {services.length > 0 && (
              <table className={`${styles["w-full"]} ${styles.table} ${styles["mt-40"]}`}>
                <thead className={`${styles["table-header"]} ${styles["text-small"]}`}>
                  <tr>
                    <th className={`${styles["top-bottom-left-rounded"]} ${styles["pl-8"]} ${styles["w-130"]}`}>Service(s)</th>
                    <th className={styles["w-100"]}>Duration</th>
                    <th className={`${styles["text-center"]} ${styles["w-80"]}`}>Qty</th>
                    <th className={`${styles["text-right"]} ${styles["w-80"]}`}>VAT (15%)</th>
                    <th
                      className={`${styles["top-bottom-right-rounded"]} ${styles["text-right"]} ${styles["pr-8"]} ${styles["w-100"]}`}
                    >
                      Price (incl. VAT)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {services.map(service => {
                    return (
                      <tr key={service.catalogueItemId} className={`${styles["table-row"]} ${styles["text-small"]}`}>
                        <td className={styles["item-name"]}>{service.name}</td>
                        <td>{getDurationText(service.duration)}</td>
                        <td className={styles["text-center"]}>{service.quantity}</td>
                        <td className={styles["text-right"]}>
                          {service.vat ? `R ${numberWithSpaces(centsToRand(getVatAmountForMPSLineItem(service)))}` : "-"}
                        </td>
                        <td className={styles["text-right"]}>
                          R{numberWithSpaces(centsToRand(getTotalAmountForMPSLineItem(service)))}
                        </td>
                      </tr>
                    );
                  })}
                  {shouldShowServicesTotal && (
                    <tr className={styles["table-total-row"]}>
                      <td>Subtotal</td>
                      <td></td>
                      <td></td>
                      <td className={styles["text-right"]}>
                        R {numberWithSpaces(centsToRand(getTotalVatAmountForMPSLineItemArray(services)))}
                      </td>
                      <td className={styles["text-right"]}>
                        R {numberWithSpaces(centsToRand(getTotalAmountForMPSLineItemArray(services)))}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Invoice Total */}
            <div className={styles["invoice-total"]}>
              <div>Invoice Total</div>
              <div className={`${styles.flex} ${styles["ml-auto"]}`}>
                <div>R {numberWithSpaces(centsToRand(getTotalVatAmountForMPSLineItemArray([...services, ...products])))}</div>
                <div className={`${styles["w-100"]} ${styles["text-right"]}`}>
                  {" "}
                  R {numberWithSpaces(centsToRand(getTotalAmountForMPSLineItemArray([...services, ...products])))}
                </div>
              </div>
            </div>

            {/* Additional Notes */}
            {additionalNotes && (
              <div className={`${styles["mt-40"]} ${styles["text-small"]}`}>
                <div className={`${styles["font-roboto-medium"]}`}>Additional Notes:</div>
                <div className={styles["mt-8"]}>{additionalNotes}</div>
              </div>
            )}
          </div>

          {/* Pay Invoice Button */}
          <div className={`${styles.flex} ${styles["justify-end"]} ${styles["mt-40"]}`}>
            {statement.statement.status !== StatementStatus.PAID && (
              <Button
                type="button"
                label="Pay Invoice"
                size="small"
                disabled={hasBlockingMissingItems || isStockAvailabilityLoading}
                onClick={() => {
                  onPayInvoice(statement.statement.id, statement.statement.documentType);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default StatementDesktop;
